import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import AdminLogin from '../pages/AdminLogin';
import AdminDashboard from '../pages/AdminDashboard'; 
import { LogOut, Terminal } from 'lucide-react';

const AdminTerminal = () => {
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Absolute security verification loop: Query server database directly using the token
  const verifyAdminStatus = async (currentUser) => {
    if (!currentUser) {
      setUser(null);
      setCheckingAuth(false);
      return;
    }

    try {
      // Look up the document inside the secure "admins" collection matching the authenticated UID
      const adminDocRef = doc(db, "admins", currentUser.uid);
      const adminDocSnap = await getDoc(adminDocRef);

      if (adminDocSnap.exists()) {
        // Validation Passed: Record found on the cloud server
        setUser(currentUser);
      } else {
        // Validation Failed: Auth account exists, but is not designated an admin record in Firestore
        console.error("Security warning: Unauthorized entity attempted console bridge.");
        await signOut(auth);
        setUser(null);
      }
    } catch (err) {
      console.error("Cryptographic authentication handshake failed:", err);
      await signOut(auth);
      setUser(null);
    } finally {
      setCheckingAuth(false);
    }
  };

  // Live Firebase auth state channel pipeline
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setCheckingAuth(true);
      verifyAdminStatus(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    setCheckingAuth(true);
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Logout runtime failure:", err);
      setCheckingAuth(false);
    }
  };

  // State 1: Security Handshake resolving or validating cloud record match
  if (checkingAuth) {
    return (
      <div className="w-full min-h-screen bg-[#fafafa] flex flex-col items-center justify-center space-y-2 font-sans antialiased">
        <Terminal size={20} className="text-slate-400 animate-pulse" />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest animate-pulse">
          Validating Security Tokens...
        </span>
      </div>
    );
  }

  // State 2: Gate Check Failed -> Enforce isolated AdminLogin viewport
  if (!user) {
    return <AdminLogin onLoginSuccess={() => verifyAdminStatus(auth.currentUser)} />;
  }

  // State 3: Server-side Authorization Confirmed -> Render dashboard environment
  return (
    <div className="relative min-h-screen bg-[#fafafa]">
      
      {/* Floating System Logout Button Hooked Globally */}
      <div className="fixed top-4 right-4 md:top-12 md:right-6 z-40">
        <button
          onClick={handleLogout}
          className="px-4 h-[34px] bg-white hover:bg-rose-50 text-slate-500 hover:text-rose-600 border border-slate-200 hover:border-rose-200 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
        >
          <LogOut size={12} /> Sign Out Terminal
        </button>
      </div>

      {/* Inject the fully controllable live data editor board */}
      <AdminDashboard />
    </div>
  );
};

export default AdminTerminal;